<template>
  <div class="batch-candidate-uploader">
    <div v-if="!hasFinishedUpload" class="upload-title">{{ $t(`${i18nPrefix}.title.uploader`) }}</div>
    <el-divider v-if="!hasFinishedUpload" class="upload-divider"></el-divider>
    <template v-for="(stepInfo, index) in stepList">
      <component
        v-bind="stepInfo.props"
        v-on="stepInfo.events"
        :ref="`stepComponent_${stepInfo.step}`"
        :key="index"
        :is="stepInfo.component"
        :show="stepInfo.step == currentStep"
        :candidateList="candidateList"
        :failList="failList"
      />
    </template>
    <DialogWrapper
      class="editor-dialog"
      close-on-cancel
      :titleI18nKey="$t(`${i18nPrefix}.btn.import`)"
      :show.sync="dialog.show"
      @save="handleDialogSaveBtnClick"
      @cancel="handleDialogCancelBtnClick">
      <div class="loading-container" v-loading="dialog.loading">
        <WrappedForm
          ref="form"
          class="editor-form"
          labelPosition="top"
          hideRequiredAsterisk
          :model="dialog.data"
          :formLines="formLines"
        >
          <template #source>
            <div class="source-code-container">
              <div class="input source-input">
                <template v-if="canAddSourceCode">
                  <el-autocomplete
                    v-model="dialog.data['source']"
                    clearable
                    :fetch-suggestions="searchSourceList"
                    :label="$t(`${i18nPrefix}.label.source`)"
                    :placeholder="$t(`${i18nPrefix}.message.sourcePlaceholder`)"
                  ></el-autocomplete>
                </template>
                <template v-else>
                  <el-select
                    v-model="dialog.data['source']"
                    clearable
                    filterable
                    :placeholder="$t(`${i18nPrefix}.message.sourcePlaceholder`)"
                    :filter-method="handleFilterSource"
                    @blur="validateSource"
                    @clear="clearSource"
                  >
                    <el-option
                      v-for="(option, index) in staticSourceList"
                      :key="index"
                      :label="option.label"
                      :value="option['source'] || option.value"
                    />
                  </el-select>
                </template>
              </div>
              <div class="tips source-tips">
                <el-tooltip popper-class="tips-content" effect="dark" :content="$t(`${i18nPrefix}.message.sourceTips`)" placement="bottom">
                  <div class="tip-icon">?</div>
                </el-tooltip>
              </div>
            </div>
          </template>
          <template #code>
            <div class="source-code-container">
              <div class="input code-input">
                <template v-if="canAddSourceCode">
                  <el-autocomplete
                    v-model="dialog.data['code']"
                    clearable
                    :fetch-suggestions="searchCodeList"
                    :label="$t(`${i18nPrefix}.label.code`)"
                    :placeholder="$t(`${i18nPrefix}.message.codePlaceholder`)"
                  ></el-autocomplete>
                </template>
                <template v-else>
                  <el-select
                    v-model="dialog.data['code']"
                    clearable
                    filterable
                    :placeholder="$t(`${i18nPrefix}.message.codePlaceholder`)"
                    :filter-method="handleFilterCode"
                    @blur="validateCode"
                    @clear="clearCode"
                  >
                    <el-option
                      v-for="(option, index) in staticCodeList"
                      :key="index"
                      :label="option.label"
                      :value="option['code'] || option.value"
                    />
                  </el-select>
                </template>
              </div>
              <div class="tips code-tips">
                <el-tooltip popper-class="tips-content" effect="dark" :content="$t(`${i18nPrefix}.message.codeTips`)" placement="bottom">
                  <div class="tip-icon">?</div>
                </el-tooltip>
              </div>
            </div>
          </template>
          <template #isPrivate>
            <div class="is-private">
              <el-switch v-model="dialog.data['commentIsPrivate']" :inactive-text="$t('admin.application.candidate.label.isPrivate')"></el-switch>
            </div>
          </template>
        </WrappedForm>
        <div class="open-question-container" v-if="questionnaire.length > 0">
          <div class="title">{{ $t(`${i18nPrefix}.label.hrOpenQuestion`) }}</div>
          <Questionnaire
            ref="openQuestionRef_hr"
            :candidateInfo="{ hr: questionnaire }"
            :questionnaireType="QUESTIONNAIRE_TYPE.OPEN_QUESTION"
            infoPropName="hr"
            displayMode="candidate-answer"
          ></Questionnaire>
        </div>
      </div>
    </DialogWrapper>
    <AIResumeParseLoadingDialog
      :isShow="showAIResumeParseDialog"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import ConfirmCandidate from './confirmCandidate.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import Uploader from './uploader.vue'
import AIResumeParseLoadingDialog from '@/components/dialog/aiResumeParseLoadingDialog.vue'
import { mapActions, mapGetters } from 'vuex'
import { addI18nProperty } from '@/i18n'
import Questionnaire, { QUESTIONNAIRE_TYPE } from '@/components/candidate/editor/questionnaire.vue'
import { generateTempFileKey, getFileExtension, uploadFile } from '@/utils/oss'

const STEP = {
  UPLOADER: 'UPLOADER',
  CONFIRM: 'CONFIRM'
}

const ResumeUploadTaskStatus = {
  UNPACK_FAIL: 'UNPACK_FAIL',
  PARSE_FAIL: 'PARSE_FAIL',
  PARSE_FAIL_NO_CONTACT_INFO: 'PARSE_FAIL_NO_CONTACT_INFO',
  PARSE_FAIL_EXISTED_HR_ACCOUNT: 'PARSE_FAIL_EXISTED_HR_ACCOUNT'
}

export default {
  components: {
    WrappedForm,
    ConfirmCandidate,
    Uploader,
    AIResumeParseLoadingDialog,
    Questionnaire,
    DialogWrapper
  },
  data () {
    return {
      i18nPrefix: 'admin.application.batchUpload',
      currentStep: STEP.UPLOADER,
      token: null,
      uploadTaskList: [],
      uploading: false,
      processing: false,
      candidateList: [],
      failList: [],
      canAddSourceCode: false,
      companyJobList: [],
      companySourceCodeList: [],
      candidateSelection: null,
      QUESTIONNAIRE_TYPE,
      questionnaire: [],
      hrQuestionRequestSequence: 0,
      hasChanged: false,
      tagList: {
        manual: [],
        smart: []
      },
      dialog: {
        show: false,
        loading: false,
        hasLoad: false,
        data: {
          onlyTalentPool: false,
          jobId: null,
          source: null,
          code: null,
          smartTag: null,
          manualTag: null,
          comment: null,
          commentIsPrivate: false,
          notification: true
        }
      },
      isSearchSource: false,
      searchedSourceListByKeyword: [],
      isSearchCode: false,
      searchedCodeListByKeyword: [],
      hasFinishedUpload: false,
      showAIResumeParseDialog: false,
      ossParseTaskId: null
    }
  },
  computed: {
    ...mapGetters('account', ['isCompanyAccount']),
    hasCRMTagPermission () {
      return this.hasAnyPermission('PREMIUM_TAG_MANAGEMENT')
    },
    staticSourceList () {
      if (this.canAddSourceCode) {
        return []
      }
      if (this.isSearchSource) {
        return _.map(this.searchedSourceListByKeyword, item => {
          return {
            label: item,
            value: item
          }
        })
      } else {
        const result = []
        _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, sourceInfo => {
            const source = sourceInfo.source
            if (!result.includes(source)) {
              result.push(source)
            }
          })
        })
        return _.map(result, item => {
          return {
            label: item,
            value: item
          }
        })
      }
    },
    staticCodeList () {
      if (this.canAddSourceCode) {
        return []
      }
      if (this.isSearchCode) {
        return _.map(this.searchedCodeListByKeyword, item => {
          return {
            label: item,
            value: item
          }
        })
      } else {
        const result = []
        const source = this.dialog.data.source
        _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, (sourceInfo) => {
            if (source && sourceInfo.source !== source) {
              return
            }
            _.forEach(sourceInfo.code_list, codeInfo => {
              const code = codeInfo.code
              if (!result.includes(code)) {
                result.push(code)
              }
            })
          })
        })

        return _.map(result, item => {
          return {
            label: item,
            value: item
          }
        })
      }
    },
    pathJobId () {
      const jobId = parseInt(this.$route.params.jobId)
      return jobId > 0 ? jobId : null
    },
    formLines () {
      const vm = this
      const lines = {
        onlyTalentPool: {
          get hidden () { return Boolean(vm.pathJobId) },
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.onlyTalentPool`),
          option: {
            data: [{ label: this.$t('common.label.yes'), value: 1 }, { label: this.$t('common.label.no'), value: 0 }]
          },
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        jobId: {
          get hidden () { return !vm.pathJobId && vm.dialog.data.onlyTalentPool },
          get disabled () { return Boolean(vm.pathJobId) },
          elType: 'el-select',
          class: 'job-select',
          label: this.$t(`${this.i18nPrefix}.label.job`),
          events: { change: this.handleJobChange },
          option: {
            data: this.companyJobList,
            grouped: true,
            groupLabelKey: 'i18nName',
            groupOptionsDataKey: 'job_list',
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: {
            filterable: true,
            popperAppendToBody: false
          },
          get rules () {
            if (!vm.pathJobId) {
              return [{ required: true, message: vm.$t('common.message.requiredFormField'), trigger: 'blur' }]
            }
            return null
          }
        },
        source: {
          get hidden () { return !vm.pathJobId && vm.dialog.data.onlyTalentPool },
          elType: 'div',
          label: this.$t(`${this.i18nPrefix}.label.source`),
          contentSlot: 'source',
          option: {
            data: this.staticSourceList
          },
          get props () {
            if (vm.canAddSourceCode) {
              return { fetchSuggestions: vm.searchSourceList, clearable: true }
            } else {
              return { filterable: true, clearable: true }
            }
          }
        },
        code: {
          get hidden () { return !vm.pathJobId && vm.dialog.data.onlyTalentPool },
          elType: 'div',
          label: this.$t(`${this.i18nPrefix}.label.code`),
          contentSlot: 'code',
          option: {
            data: this.staticCodeList
          },
          get props () {
            if (vm.canAddSourceCode) {
              return { fetchSuggestions: vm.searchCodeList, clearable: true }
            } else {
              return { filterable: true, clearable: true }
            }
          }
        },
        smartTag: {
          hidden: true,
          elType: 'el-select',
          class: 'smart-tag-select',
          label: this.$t(`${this.i18nPrefix}.label.tag.smart`),
          option: {
            data: this.tagList.smart,
            labelKey: 'name',
            valueKey: 'id'
          },
          props: { clearable: true, filterable: true, multiple: true }
        },
        manualTag: {
          class: 'manual-tag-select',
          hidden: !this.hasCRMTagPermission,
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.tag.manual`),
          option: {
            data: _.sortBy(this.tagList.manual, 'name'),
            labelKey: 'name',
            valueKey: 'id'
          },
          props: { clearable: true, filterable: true, multiple: true }
        },
        comment: {
          class: 'comment-input',
          label: this.$t(`${this.i18nPrefix}.label.comment`),
          props: { type: 'textarea', autosize: { minRows: 5 } },
          contentExtraSlot: 'isPrivate'
        }
        // 2024-08-08 移除批量上传是否发送提醒的勾选框，默认发送提醒
        // notification: {
        //   class: 'notification',
        //   elType: 'el-checkbox',
        //   checkboxLabel: this.$t(`${this.i18nPrefix}.label.notification`)
        // }
      }
      return lines
    },
    stepList () {
      const infoList = [
        {
          step: STEP.UPLOADER,
          component: Uploader,
          events: {
            uploadFile: this.handUploadFile
          },
          props: {
            uploadTaskList: this.uploadTaskList,
            uploading: this.uploading
          }
        },
        {
          step: STEP.CONFIRM,
          component: ConfirmCandidate,
          events: {
            importCandidate: this.handleCandidateImport
          }
        }
      ]
      return infoList
    },
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    },
    useAIResumeParse () {
      const hasAIResumeParsePermission = this.hasAnyPermission('PREMIUM_AI_RESUME_PARSE')
      if (this.isCompanyAccount && !hasAIResumeParsePermission) {
        return false
      }
      return true
    }
  },
  created () {

  },
  mounted () {
    window.onbeforeunload = () => {
      return (this.processing || this.hasChanged) ? '' : null
    }
  },
  destroyed () {
    window.onbeforeunload = null
  },
  beforeRouteLeave (to, from, next) {
    let canLeave = true
    if (this.processing) {
      canLeave = window.confirm(this.$t(`${this.i18nPrefix}.message.leavePageTip`))
    }
    next(canLeave)
  },
  methods: {
    ...mapActions('sourceCode', ['getCompanySourceCode']),
    ...mapActions('company', ['getCompanyGroupedJobList']),
    ...mapActions('crm', ['searchTag']),
    ...mapActions('common', ['getOSSUploadToken']),
    ...mapActions('application', [
      'importBatchUploadedCandidate',
      'getHrQuestion',
      'submitBatchOssParseResume',
      'getBatchOssParseResult'
    ]),
    handleFilterSource (keywords) {
      if (keywords) {
        this.$set(this.dialog.data, 'source', keywords)
        let result = []
        _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, sourceInfo => {
            const source = sourceInfo.source
            const searched = keywords ? (source.toUpperCase().includes(keywords.toUpperCase()) || sourceInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
            if (!result.includes(source) && searched) {
              result.push(source)
            }
          })
        })
        result = _.sortBy(result)
        this.searchedSourceListByKeyword = result
        this.isSearchSource = true
      } else {
        this.$set(this.dialog.data, 'source', null)
        this.isSearchSource = false
      }
    },
    handleFilterCode (keywords) {
      if (keywords) {
        this.$set(this.dialog.data, 'code', keywords)
        let result = []
        const source = this.dialog.data.source
        _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, (sourceInfo) => {
            if (source && sourceInfo.source !== source) {
              return
            }
            _.forEach(sourceInfo.code_list, codeInfo => {
              const code = codeInfo.code
              const searched = keywords ? (code.toUpperCase().includes(keywords.toUpperCase()) || codeInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
              if (!result.includes(code) && searched) {
                result.push(code)
              }
            })
          })
        })
        result = _.sortBy(result)
        this.searchedCodeListByKeyword = result
        this.isSearchCode = true
      } else {
        this.$set(this.dialog.data, 'code', null)
        this.isSearchCode = false
      }
    },
    clearSource () {
      this.$set(this.dialog.data, 'source', null)
      this.isSearchSource = false
    },
    clearCode () {
      this.$set(this.dialog.data, 'code', null)
      this.isSearchCode = false
    },
    validateSource () {
      const hasSource = _.includes(this.searchedSourceListByKeyword, this.dialog.data.source)
      if (!this.canAddSourceCode && !hasSource && this.isSearchSource) {
        this.$set(this.dialog.data, 'source', null)
        this.isSearchSource = false
      }
    },
    validateCode () {
      const hasCode = _.includes(this.searchedCodeListByKeyword, this.dialog.data.code)
      if (!this.canAddSourceCode && !hasCode && this.isSearchCode) {
        this.$set(this.dialog.data, 'code', null)
        this.isSearchCode = false
      }
    },
    async loadSourceCodeList () {
      const resp = await this.getCompanySourceCode()
      if (resp) {
        this.canAddSourceCode = resp.can_add
        this.companySourceCodeList = resp.result
      }
    },
    async loadJobList () {
      const params = {
        status: 1,
        add_candidate: 1
      }
      const resp = await this.getCompanyGroupedJobList(params)
      if (resp) {
        this.companyJobList = _.map(resp, company => {
          addI18nProperty(company, 'i18nName', 'cn_name', 'name')
          company.job_list = _.map(company.job_list, job => {
            job.cn_title = `${job.id} - ${job.cn_title}`
            job.title = `${job.id} - ${job.title}`
            addI18nProperty(job, 'i18nName', 'cn_title', 'title')
            return job
          })
          return company
        })
      }
    },
    async loadTagList () {
      if (!this.hasCRMTagPermission) {
        return
      }
      const params = {
        is_company_tag: 1,
        tag_category: ['smart', 'manual'].join(',')
      }
      const resp = await this.searchTag(params)
      if (resp) {
        this.tagList = _.groupBy(resp, 'category')
      }
    },
    async loadHrQuestion () {
      const jobId = this.dialog.data.jobId
      const requestSequence = ++this.hrQuestionRequestSequence
      this.questionnaire = []
      if (!jobId) {
        return
      }
      const resp = await this.getHrQuestion(jobId)
      if (requestSequence === this.hrQuestionRequestSequence && this.dialog.data.jobId === jobId) {
        this.questionnaire = resp || []
      }
    },
    async handleJobChange () {
      this.dialog.loading = true
      await this.loadHrQuestion()
      this.dialog.loading = false
    },
    async loadDialogData () {
      const dialog = this.dialog
      if (dialog.loading || dialog.hasLoad) {
        return
      }
      dialog.loading = true
      await Promise.all([this.loadSourceCodeList(), this.loadJobList(), this.loadTagList(), this.loadHrQuestion()])
      dialog.loading = false
      dialog.hasLoad = true
    },
    searchSourceList (keywords, callback) {
      let result = []
      _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
        _.forEach(sourceList, sourceInfo => {
          const source = sourceInfo.source
          const searched = keywords ? (source.toUpperCase().includes(keywords.toUpperCase()) || sourceInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
          if (!result.includes(source) && searched) {
            result.push(source)
          }
        })
      })
      result = _.sortBy(result)
      callback(_.map(result, val => { return { value: val } }))
    },
    searchCodeList (keywords, callback) {
      let result = []
      const source = this.dialog.data.source
      _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
        _.forEach(sourceList, (sourceInfo) => {
          if (source && sourceInfo.source !== source) {
            return
          }
          _.forEach(sourceInfo.code_list, codeInfo => {
            const code = codeInfo.code
            const searched = keywords ? (code.toUpperCase().includes(keywords.toUpperCase()) || codeInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
            if (!result.includes(code) && searched) {
              result.push(code)
            }
          })
        })
      })
      result = _.sortBy(result)
      callback(_.map(result, val => { return { value: val } }))
    },
    handleDialogCancelBtnClick () {
      this.dialog.show = false
    },
    async handleDialogSaveBtnClick () {
      const isValid = await this.$refs.form.submit()
      const openQuestionRef = this.$refs.openQuestionRef_hr
      let hrOpenQuestion = null
      if (openQuestionRef) {
        const isOpenQuestionValid = Boolean(await openQuestionRef.submit())
        if (!isOpenQuestionValid) {
          return
        }
        hrOpenQuestion = this.questionnaire
      }
      if (!isValid) {
        return
      }

      const dialog = this.dialog
      const dialogData = dialog.data
      dialog.show = false

      const importCandidateParams = {
        only_talent_pool: dialogData.onlyTalentPool,
        job_id: dialogData.jobId || '-1',
        source: dialogData.source,
        code: dialogData.code,
        comment: dialogData.comment,
        comment_is_private: dialogData.commentIsPrivate,
        tag_ids: _.chain(dialogData.smartTag).concat(dialogData.manualTag).filter().join().value(),
        email_notification: dialogData.notification,
        upload_token: this.token,
        parsed_task_list: this.candidateSelection,
        open_question: {
          hr: hrOpenQuestion
        }
      }
      const resp = await this.importBatchUploadedCandidate(importCandidateParams)
      if (resp.success) {
        this.$message.success(this.$t(`${this.i18nPrefix}.message.importSuccess`))
        setTimeout(() => {
          if (this.$route.query.from_applicant === '1') {
            this.$router.push({ path: '/v-admin/application/-1/batch-upload/history?from_applicant=1' })
          } else if (this.$route.query.talent_pool) {
            this.$router.push({ path: `/v-admin/application/-1/batch-upload/history?talent_pool=${this.$route.query.talent_pool}` })
          } else {
            this.$router.push({ path: `/v-admin/application/${dialogData.jobId}/batch-upload/history` })
          }
        }, 1000)
      }
    },
    async handUploadFile (fileList, clearBrowserFilesInputValue, clearFileList) {
      this.uploading = true
      this.hasChanged = true
      const uploadTaskList = []
      _.forEach(fileList, file => {
        uploadTaskList.push({
          file,
          taskId: null,
          finished: false,
          uploading: false,
          percentage: 0,
          ossFileKey: null
        })
      })
      this.uploadTaskList = uploadTaskList
      clearFileList()
      // 上传所有文件到 OSS
      await this.getOSSUploadToken()
      await Promise.all(_.map(uploadTaskList, uploadTask => this.uploadFileToOss(uploadTask, uploadTask.file)))
      // 所有文件上传完成后，提交 OSS 解析任务
      await this.submitOssParseTask(uploadTaskList)
      this.uploadTaskList = []
      this.uploading = false
      clearBrowserFilesInputValue()
    },
    async uploadFileToOss (uploadTask, file) {
      const fileExt = getFileExtension(file)
      const fileKey = generateTempFileKey(fileExt)
      uploadTask.uploading = true
      uploadTask.ossFileKey = fileKey
      try {
        await uploadFile(file, fileKey)
        uploadTask.percentage = 100
        uploadTask.uploading = false
        uploadTask.finished = true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Upload file to OSS failed:', error)
        uploadTask.uploading = false
        this.$message.error(this.$t(`${this.i18nPrefix}.message.uploadFailed`))
      }
    },
    async submitOssParseTask (uploadTaskList) {
      // 收集所有上传完成的文件 OSS 路径
      const fileKeys = _.chain(uploadTaskList)
        .filter(uploadTask => uploadTask.finished && uploadTask.ossFileKey)
        .map(uploadTask => uploadTask.ossFileKey)
        .value()

      if (fileKeys.length === 0) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.uploadFailed`))
        return
      }

      // 提交 OSS 解析任务
      const submitParams = {
        oss_file_list: fileKeys,
        job_id: this.pathJobId
      }
      const submitResp = await this.submitBatchOssParseResume(submitParams)
      if (submitResp && submitResp.task_id) {
        this.ossParseTaskId = submitResp.task_id
        if (this.useAIResumeParse) {
          // 显示 AI 解析加载动画
          this.showAIResumeParseDialog = true
        } else {
          this.$eventBus.$emit('showLoading', true)
        }
        // 开始轮询解析结果
        await this.getOssParseResult(uploadTaskList)
      }
    },
    async getOssParseResult (uploadTaskList) {
      const resp = await this.getBatchOssParseResult({ task_id: this.ossParseTaskId })
      if (resp.is_finished) {
        if (this.useAIResumeParse) {
          // 关闭 AI 解析加载动画
          this.showAIResumeParseDialog = false
        } else {
          this.$eventBus.$emit('showLoading', false)
        }
        // 处理解析结果
        _.forEach(uploadTaskList, uploadTask => {
          const { file } = uploadTask
          const resumeInfoList = resp.resume_info || []
          const matchedResume = _.find(resumeInfoList, info => info.oss_key === uploadTask.ossFileKey)
          if (matchedResume) {
            if (ResumeUploadTaskStatus[matchedResume.status]) {
              this.failList.push({ status: matchedResume.status, file_name: file.name })
            } else {
              this.candidateList.push({
                resume_info: matchedResume,
                upload_task_id: uploadTask.taskId,
                origin_oss_key: matchedResume.origin_oss_key,
                oss_key: uploadTask.ossFileKey,
                file_name: file.name.endsWith('.zip') ? matchedResume.file_name : file.name,
                file_size: file.size,
                file_type: file.type,
                unpacked_file_key: file.name.endsWith('.zip') ? matchedResume.unpacked_file_key : null,
                showDuplicate: false
              })
            }
          } else {
            // 对于 zip 文件，可能有多个解析结果
            const zipResume = _.filter(resumeInfoList, info => info.origin_oss_key === uploadTask.ossFileKey)
            _.forEach(zipResume, resume => {
              if (ResumeUploadTaskStatus[resume.status]) {
                this.failList.push({ status: resume.status, file_name: resume.file_name || file.name })
              } else {
                this.candidateList.push({
                  resume_info: resume,
                  upload_task_id: uploadTask.taskId,
                  origin_oss_key: resume.origin_oss_key,
                  oss_key: resume.oss_key,
                  file_name: resume.file_name || file.name,
                  file_size: file.size,
                  file_type: file.type,
                  unpacked_file_key: resume.unpacked_file_key,
                  showDuplicate: false
                })
              }
            })
          }
        })
        this.currentStep = STEP.CONFIRM
        this.hasFinishedUpload = true
        this.$message.success(this.$t(`${this.i18nPrefix}.message.uploadFinished`))
      } else {
        // 继续轮询
        setTimeout(() => {
          this.getOssParseResult(uploadTaskList)
        }, 2000)
      }
    },
    async handleCandidateImport (candidateSelection) {
      if (candidateSelection.length < 1) {
        this.$message.error(this.$t('admin.application.batchUpload.message.noCandidate'))
        return
      }
      this.candidateSelection = candidateSelection
      this.dialog.show = true
      this.dialog.data = { onlyTalentPool: this.pathJobId ? 0 : 1, jobId: this.pathJobId, notification: true }
      await this.loadDialogData()
    }
  }
}
</script>

<style lang="scss" scoped>
.batch-candidate-uploader {

  .upload-title {
    font-size: 18px;
    font-weight: 500;
    color: #303133;
  }

  .upload-divider {
    color: #E4E7ED;
    margin: 18px 0 0 0;
  }
  .editor-dialog {
    ::v-deep {

      .el-dialog__body {
        .el-divider {
          margin: 20px 0 20px;
        }

        .loading-container {
          .form-container {

            .job-select {
              width: 94%;
              .el-select-dropdown {
                max-width: 48%;
              }
            }

            .smart-tag-select {
              width: 94%;
            }

            .manual-tag-select {
              width: 94%;
            }

            .comment-input {
              width: 94%;
            }

            .notification {
              .el-form-item__label {
                display: none;
              }
            }
          }

          .is-private {
            margin-top: 10px;
            line-height: 20px;

            .el-switch {
              .el-switch__label {
                color: #606266;

                &.is-active {
                  color: #606266;
                }
              }
            }
          }
        }
      }
    }

    .open-question-container {
      ::v-deep {
        .question-container {
          .question-title-container {
            .question-title {
              font-size: 14px;
            }
          }
        }
      }
    }

    .source-code-container {
      display: flex;

      .input {
        width: 94%;
      }

      .tips {
        width: 5%;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 10px;

        .tip-icon {
          cursor: help;
          border: 1px solid #aeaeae;
          color: #aeaeae;
          font-size: 14px;
          font-weight: 400;
          display: flex;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          align-items: center;
          justify-content: center;
        }
      }
    }

    .editor-footer {
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
